import { LangLanguageEnum } from '../generated/api/enumLib_api';

export const possibleLanguages = [
  'De',
  'Da',
  'En',
  'Es',
  'Fo',
  'Fr',
  'No',
  'Pl',
  'Se',
] as const;

export type PossibleLangCode = (typeof possibleLanguages)[number];

export function isPossibleLangCode(code: unknown): code is PossibleLangCode {
  return (
    typeof code === 'string' &&
    possibleLanguages.includes(code as PossibleLangCode)
  );
}

export const fallback = (lang: PossibleLangCode): PossibleLangCode => {
  switch (lang) {
    case 'Fo':
    case 'Se':
    case 'No':
      return 'Da';
    default:
      return 'En';
  }
};

// The shipped, user-selectable languages: enum value -> lang code.
const shippedLangLanguageEnumToPossibleLangCode = {
  [LangLanguageEnum.da]: 'Da',
  [LangLanguageEnum.de]: 'De',
  [LangLanguageEnum.en]: 'En',
  [LangLanguageEnum.es]: 'Es',
  [LangLanguageEnum.fo]: 'Fo',
  [LangLanguageEnum.no]: 'No',
  [LangLanguageEnum.pl]: 'Pl',
  [LangLanguageEnum.se]: 'Se',
  [LangLanguageEnum.fr]: 'Fr',
} as const;

// Ar and Ru are retired: still enum values (so persisted "Ar"/"Ru" values keep
// resolving) but no longer offered as UI languages. Map them to the English
// fallback so the map stays a total Record<LangLanguageEnum, …> and lookups
// never return undefined.
export const langLanguageEnumToPossibleLangCode = {
  ...shippedLangLanguageEnumToPossibleLangCode,
  [LangLanguageEnum.ar]: 'En',
  [LangLanguageEnum.ru]: 'En',
} as const satisfies Record<LangLanguageEnum, PossibleLangCode>;

// Built from the shipped set only, so a real code (e.g. 'En') is never claimed
// by a retired language when the map is inverted.
export const possibleLangCodeToLangLanguageEnum = Object.fromEntries(
  Object.entries(shippedLangLanguageEnumToPossibleLangCode).map(([k, v]) => [
    v,
    k,
  ])
) as unknown as Record<PossibleLangCode, LangLanguageEnum>;

export type PossibleAppCode =
  | 'MyPlan'
  | 'TeamPlan'
  | 'FunctionPlan'
  | 'ClockPoint'
  | 'Shared';

export const possibleAppCodes: PossibleAppCode[] = [
  'MyPlan',
  'TeamPlan',
  'FunctionPlan',
  'ClockPoint',
  'Shared',
];
